import type { StorageAdapter } from "@platform/storage";
import { ONBOARDING_STORAGE_KEY } from "@data/settings";

export interface OnboardingDeps {
  element: HTMLElement;
  steps: HTMLElement[];
  dots: HTMLElement[];
  nextButton: HTMLButtonElement;
  skipButton: HTMLButtonElement;
  storage: StorageAdapter;
}

/** Onboarding overlay — серия из 3 шагов для первого запуска. */
export class OnboardingOverlay {
  private currentStep = 1;
  private readonly totalSteps: number;

  constructor(private readonly deps: OnboardingDeps) {
    this.totalSteps = deps.steps.length;
  }

  /** Показывает overlay если onboarding ещё не был показан. */
  async maybeShow(): Promise<void> {
    const shown = await this.deps.storage.get<boolean>(ONBOARDING_STORAGE_KEY);
    if (shown) return;
    this.show();
  }

  private show(): void {
    this.currentStep = 1;
    this.updateStepVisibility();
    this.deps.element.classList.add("is-visible");
    this.deps.element.setAttribute("aria-hidden", "false");

    const handleNext = () => {
      this.next();
    };
    const handleSkip = () => {
      this.hide();
    };

    this.deps.nextButton.addEventListener("click", handleNext);
    this.deps.skipButton.addEventListener("click", handleSkip);

    // Сохраним ссылки для последующей очистки
    this._nextHandler = handleNext;
    this._skipHandler = handleSkip;
  }

  private _nextHandler: (() => void) | null = null;
  private _skipHandler: (() => void) | null = null;

  private next(): void {
    if (this.currentStep < this.totalSteps) {
      this.currentStep += 1;
      this.updateStepVisibility();
    } else {
      this.hide();
    }

    // На последнем шаге меняем текст кнопки
    if (this.currentStep >= this.totalSteps) {
      this.deps.nextButton.textContent = "Let's go";
    }
  }

  private hide(): void {
    this.deps.element.classList.remove("is-visible");
    this.deps.element.setAttribute("aria-hidden", "true");

    if (this._nextHandler) {
      this.deps.nextButton.removeEventListener("click", this._nextHandler);
      this._nextHandler = null;
    }
    if (this._skipHandler) {
      this.deps.skipButton.removeEventListener("click", this._skipHandler);
      this._skipHandler = null;
    }

    // Сбрасываем текст кнопки
    this.deps.nextButton.textContent = "Next";

    void this.deps.storage.set<boolean>(ONBOARDING_STORAGE_KEY, true);
  }

  private updateStepVisibility(): void {
    for (let i = 0; i < this.deps.steps.length; i++) {
      const step = this.deps.steps[i];
      if (!step) continue;
      if (i + 1 === this.currentStep) {
        step.removeAttribute("hidden");
      } else {
        step.setAttribute("hidden", "");
      }
    }

    for (let i = 0; i < this.deps.dots.length; i++) {
      const dot = this.deps.dots[i];
      if (!dot) continue;
      dot.classList.toggle("is-active", i + 1 === this.currentStep);
    }
  }
}
